import { createFileRoute } from '@tanstack/react-router'
import { businessNumberLabel, localeFor } from '@/lib/locale'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Loader2, ShieldCheck, Download, CreditCard, Check } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { phFormat, peso } from '@/lib/ph'
import { FEATURES } from '@/lib/features'

export const Route = createFileRoute('/i/$token')({ component: PublicInvoice })

interface PublicInvoiceData {
  invoice: {
    invoice_number: string | null; title: string | null; status: string; currency: string
    subtotal: number; tax: number; total: number; amount_paid: number
    // Spec 025 · a progress claim deducts retention AFTER GST, so subtotal + tax ≠ total on these.
    claim_number?: number | null; retention_pct?: number | null; retention_amount?: number | null
    is_retention_release?: boolean | null
    issue_date: string | null; due_date: string | null; stripe_payment_link: string | null; viewed_at: string | null
  
    photo_urls: string[] | null}
  company: { name: string | null; logo: string | null; accent_color: string | null; abn: string | null; email: string | null; phone: string | null; country: string | null; payment_qr_url?: string | null; stripe_enabled?: boolean; bank_account_name?: string | null; bank_bsb?: string | null; bank_account_number?: string | null; payment_instructions?: string | null }
  client: { first_name: string | null; last_name: string | null; organization: string | null }
  items: Array<{ description: string; quantity: number; unit_price: number; amount: number }>
}

// Spec 001: MySerbisyo bills in pesos only. The currency argument is kept for the call sites.
const money = (n: number, _c?: string) => peso(n)

function PublicInvoice() {
  const { token } = Route.useParams()
  const [paying, setPaying] = useState(false)

  const payByCard = async () => {
    setPaying(true)
    const { data, error } = await supabase.functions.invoke('stripe-checkout', { body: { token, origin: window.location.origin } })
    const url = (data as { url?: string })?.url
    if (error || !url) { setPaying(false); return toast.error('Could not start payment. Please try again or contact the business.') }
    window.location.href = url
  }

  const q = useQuery({
    queryKey: ['public-invoice', token],
    queryFn: async (): Promise<PublicInvoiceData | null> => {
      const { data, error } = await supabase.rpc('public_get_invoice', { p_token: token })
      if (error) throw error
      return data as PublicInvoiceData | null
    },
  })

  useEffect(() => { if (q.data) supabase.rpc('public_mark_invoice_viewed', { p_token: token }) }, [q.data, token])

  // Returned from Stripe Checkout: confirm + poll for the webhook to mark it paid.
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('paid') !== '1') return
    toast.success('Payment received — thank you!')
    window.history.replaceState({}, '', window.location.pathname)
    const t = setInterval(() => q.refetch(), 1500)
    const stop = setTimeout(() => clearInterval(t), 12000)
    return () => { clearInterval(t); clearTimeout(stop) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (q.isLoading) return <div className="flex min-h-screen items-center justify-center bg-canvas-subtle"><Loader2 className="h-6 w-6 animate-spin text-token-muted" /></div>
  if (!q.data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas-subtle px-4 text-center">
        <div><p className="font-display text-[20px] font-bold text-token-primary">Invoice not found</p><p className="mt-1 text-[14px] text-token-secondary">This link may have expired or been withdrawn.</p></div>
      </div>
    )
  }

  const { invoice, company, client, items } = q.data
  const accent = company.accent_color || '#0038a8'
  const cur = invoice.currency || 'PHP'
  const companyName = (company.name || 'Your business').trim()
  const clientName = [client.first_name, client.last_name].filter(Boolean).join(' ').trim() || client.organization || 'Customer'
  const due = Math.max(0, Number(invoice.total) - Number(invoice.amount_paid))
  const paid = invoice.status === 'paid' || due <= 0

  return (
    <div className="min-h-screen bg-canvas-subtle px-4 py-8 print:bg-token-surface print:py-0 sm:py-12">
      <div className="mx-auto max-w-3xl">
        <div className="mb-5 flex items-center justify-between print:hidden">
          <div className="flex items-center gap-2.5">
            {company.logo
              ? <img src={company.logo} alt={companyName} className="h-9 w-9 rounded-[10px] object-cover" />
              : <span className="flex h-9 w-9 items-center justify-center rounded-[10px] text-[16px] font-bold text-white" style={{ background: accent }}>{companyName[0]?.toUpperCase()}</span>}
            <div>
              <p className="font-display text-[17px] font-bold tracking-[-0.02em] text-token-primary">{companyName}</p>
              {company.abn && <p className="text-[11.5px] text-token-secondary">{businessNumberLabel(company)} {company.abn}</p>}
            </div>
          </div>
          <button onClick={() => window.print()} className="inline-flex h-9 items-center gap-1.5 rounded-[10px] border border-token-subtle bg-token-surface px-3.5 text-[13px] font-semibold text-token-primary hover:bg-canvas-subtle">
            <Download className="h-4 w-4" /> PDF
          </button>
        </div>

        <div className="overflow-hidden rounded-[16px] border border-token-subtle bg-token-surface shadow-[0_1px_3px_rgba(16,24,40,0.06)] print:border-0 print:shadow-none">
          {paid && (
            <div className="flex items-center gap-2 bg-token-success-surface px-6 py-2.5 text-[13px] font-bold text-token-success"><Check className="h-4 w-4" /> Paid in full — thank you</div>
          )}
          <div className="p-6 sm:p-10">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-token-muted">Invoice</p>
                <h1 className="font-display mt-1 text-[24px] font-bold tracking-[-0.02em] text-token-primary">{invoice.title || 'Invoice'}</h1>
              </div>
              <div className="text-right text-[12px] text-token-secondary">
                <p className="text-[13px] font-bold tabular-nums text-token-primary">{invoice.invoice_number}</p>
                {invoice.issue_date && <p className="mt-0.5">Issued {phFormat(new Date(invoice.issue_date), 'date')}</p>}
                {invoice.due_date && <p>Due {phFormat(new Date(invoice.due_date), 'date')}</p>}
              </div>
            </div>

            <div className="mt-7 grid grid-cols-2 gap-6 border-t border-token-subtle pt-5 text-[13px]">
              <div><p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-token-muted">Bill to</p><p className="mt-1 font-semibold text-token-primary">{clientName}</p></div>
              <div className="text-right"><p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-token-muted">From</p><p className="mt-1 font-semibold text-token-primary">{companyName}</p>{company.email && <p className="text-[12px] text-token-secondary">{company.email}</p>}{company.phone && <p className="text-[12px] text-token-secondary">{company.phone}</p>}</div>
            </div>

            <div className="mt-7 overflow-x-auto">
            <table className="w-full min-w-[420px] border-separate border-spacing-0 text-[13px]">
              <thead><tr className="text-[11px] font-semibold uppercase tracking-[0.06em] text-token-muted">
                <th className="border-b border-token-subtle pb-2 text-left">Description</th>
                <th className="border-b border-token-subtle pb-2 text-right">Qty</th>
                <th className="border-b border-token-subtle pb-2 text-right">Unit</th>
                <th className="border-b border-token-subtle pb-2 text-right">Amount</th>
              </tr></thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={i}>
                    <td className="border-b border-token-subtle py-2.5 pr-2 text-token-primary">{it.description}</td>
                    <td className="whitespace-nowrap border-b border-token-subtle py-2.5 pl-2 text-right tabular-nums text-token-secondary">{Number(it.quantity)}</td>
                    <td className="whitespace-nowrap border-b border-token-subtle py-2.5 pl-2 text-right tabular-nums text-token-secondary">{money(it.unit_price, cur)}</td>
                    <td className="whitespace-nowrap border-b border-token-subtle py-2.5 pl-2 text-right font-semibold tabular-nums text-token-primary">{money(it.amount, cur)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>

            <div className="mt-6 flex justify-end">
              <div className="w-full max-w-[260px] space-y-2 text-[13px]">
                <div className="flex justify-between"><span className="text-token-secondary">Subtotal</span><span className="tabular-nums text-token-primary">{money(invoice.subtotal, cur)}</span></div>
                {invoice.tax > 0 && <div className="flex justify-between"><span className="text-token-secondary">{localeFor(company).taxLabel}</span><span className="tabular-nums text-token-primary">{money(invoice.tax, cur)}</span></div>}
                {/* Spec 025 — without this line the customer sees a total that is less than
                    subtotal + GST with no explanation, which reads as a mistake and invites a
                    dispute over an amount that is correct. */}
                {Number(invoice.retention_amount) > 0 && (
                  <div className="flex justify-between">
                    <span className="text-token-secondary">
                      Less retention{invoice.retention_pct ? ` (${Number(invoice.retention_pct)}%)` : ''}
                    </span>
                    <span className="tabular-nums text-token-primary">−{money(Number(invoice.retention_amount), cur)}</span>
                  </div>
                )}
                <div className="flex justify-between border-t border-token-subtle pt-2 text-[15px] font-bold"><span className="text-token-primary">Total</span><span className="font-display tabular-nums text-token-primary">{money(invoice.total, cur)}</span></div>
                {Number(invoice.amount_paid) > 0 && !paid && <div className="flex justify-between text-token-success"><span>Paid</span><span className="tabular-nums">−{money(invoice.amount_paid, cur)}</span></div>}
                {!paid && <div className="flex justify-between text-[15px] font-bold"><span style={{ color: accent }}>Amount due</span><span className="font-display tabular-nums" style={{ color: accent }}>{money(due, cur)}</span></div>}
              </div>
            </div>
          </div>

          {!paid && (
            <div className="border-t border-token-subtle bg-token-surface p-6 print:hidden sm:p-8">
              {FEATURES.stripePayments && company.stripe_enabled ? (
                <button onClick={payByCard} disabled={paying} className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-[12px] text-[15px] font-bold text-white disabled:opacity-70 sm:w-auto sm:px-8" style={{ background: accent }}>
                  {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} {paying ? 'Redirecting…' : `Pay ${money(due, cur)}`}
                </button>
              ) : FEATURES.stripePayments && invoice.stripe_payment_link ? (
                <a href={invoice.stripe_payment_link} className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-[12px] text-[15px] font-bold text-white sm:w-auto sm:px-8" style={{ background: accent }}>
                  <CreditCard className="h-4 w-4" /> Pay {money(due, cur)}
                </a>
              ) : !(company.bank_account_name || company.bank_account_number) ? (
                <p className="text-[13px] text-token-secondary">To pay this invoice, please contact {companyName}{company.email ? ` at ${company.email}` : ''}.</p>
              ) : null}

              {(invoice.photo_urls?.length ?? 0) > 0 && (
                <div className="mt-7">
                  <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-token-muted">Photos from your job</p>
                  <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
                    {invoice.photo_urls!.map((u) => (
                      <a key={u} href={u} target="_blank" rel="noreferrer">
                        <img src={u} alt="Job photo" className="h-20 w-full rounded-[10px] border border-token-subtle object-cover" loading="lazy" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Spec 045. A payment QR (GCash / Maya / PayID). The payment is not confirmed
                  automatically; the business marks the invoice paid, as with a bank transfer. */}
              {company.payment_qr_url && (
                <div className="mt-5 rounded-[12px] border border-token-warning bg-token-warning-surface p-4 text-center">
                  <p className="text-[13.5px] font-bold text-token-primary">Scan to pay</p>
                  <img src={company.payment_qr_url} alt="Payment QR code" className="mx-auto mt-2 h-40 w-40 rounded-[10px] border border-token-subtle bg-token-surface object-contain p-1" />
                  <p className="mt-2 text-[12.5px] text-token-secondary">GCash, Maya or your banking app. Use the invoice number as the reference.</p>
                </div>
              )}
              {(company.bank_account_name || company.bank_account_number) && (
              <div className="mt-5 rounded-[12px] border border-token-warning bg-token-warning-surface p-4 text-[13.5px] text-token-primary">
                  <p className="font-bold text-token-primary">{company.stripe_enabled || invoice.stripe_payment_link ? 'Or pay by bank transfer' : 'Pay by bank transfer'}</p>
                  <div className="mt-2 space-y-0.5">
                    {company.bank_account_name && <div><span className="block w-auto text-token-secondary sm:inline-block sm:w-32">Account name</span> <span className="font-semibold text-token-primary">{company.bank_account_name}</span></div>}
                    {company.bank_bsb && <div><span className="block w-auto text-token-secondary sm:inline-block sm:w-32">BSB</span> <span className="font-semibold tabular-nums text-token-primary">{company.bank_bsb}</span></div>}
                    {company.bank_account_number && <div><span className="block w-auto text-token-secondary sm:inline-block sm:w-32">Account number</span> <span className="font-semibold tabular-nums text-token-primary">{company.bank_account_number}</span></div>}
                  </div>
                  <p className="mt-2 text-[12.5px] text-token-secondary">{company.payment_instructions || 'Please include your invoice number as the payment reference.'}</p>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-1.5 print:hidden">
          <a href="/portal" className="text-[12.5px] font-semibold text-token-secondary underline hover:text-token-primary">View all your quotes &amp; invoices →</a>
          <span className="inline-flex items-center gap-1.5 text-[12px] text-token-muted"><ShieldCheck className="h-3.5 w-3.5" /> Secured by MySerbisyo</span>
        </div>
      </div>
    </div>
  )
}
